import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import {
  extensionFunctionType,
  extensionWebhookType,
} from '@rossum/api-client/hooks';
import { IconCirclePlus } from '@rossum/ui/icons/tabler';
import {
  Button,
  Link,
  Paper,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import {
  FUNCTIONS_LINK,
  WEBHOOKS_LINK,
} from '../../../../../../constants/values';
import { linebreak } from '../../../../../../lib/formaterValues';
import { extensionsCreatePath } from '../../../../routes';
import { CreatableHookType } from '../../../create-extension/components/ExtensionTypeSelect';
import {
  ExtensionFunctionSchemaSvg,
  ExtensionWebhookSvg,
} from './ExtensionTypeSchemasSvgs';

type FirstExtensionProps = {
  extensionType: CreatableHookType;
};

const bannerImages: Record<CreatableHookType, () => JSX.Element> = {
  [extensionFunctionType]: ExtensionFunctionSchemaSvg,
  [extensionWebhookType]: ExtensionWebhookSvg,
};

const FirstExtension = ({ extensionType }: FirstExtensionProps) => {
  const BannerImage = bannerImages[extensionType];

  return (
    <Stack
      component={Paper}
      flex={1}
      justifyContent="center"
      alignItems="center"
      spacing={2}
      sx={{ p: 4 }}
    >
      <BannerImage />
      <Stack justifyContent="center" alignItems="center" spacing={0.5}>
        <Typography variant="h6">
          <FormattedMessage
            id={`containers.settings.extensions.intro.firstExtension.description.title.${extensionType}`}
          />
        </Typography>
        <Typography
          variant="body2"
          color="secondary"
          sx={{ textAlign: 'center' }}
        >
          <FormattedMessage
            id={`containers.settings.extensions.intro.firstExtension.description.text.${extensionType}`}
            values={{ linebreak }}
          />
        </Typography>
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: 1, maxWidth: '400px' }}
      >
        <Link
          target="_blank"
          color="textPrimary"
          href={
            extensionType === extensionFunctionType
              ? FUNCTIONS_LINK
              : WEBHOOKS_LINK
          }
        >
          <FormattedMessage id="containers.settings.extensions.intro.firstExtension.readMore" />
        </Link>

        <Button
          component={RouterLink}
          to={`${extensionsCreatePath()}#${extensionType}`}
          variant="outlined"
          color="secondary"
          startIcon={
            <SvgIcon fontSize="large">
              <IconCirclePlus />
            </SvgIcon>
          }
          data-cy={`extensions-create-first-button-intro-banner-${extensionType}`}
          size="small"
        >
          <FormattedMessage
            id={`containers.settings.extensions.intro.firstExtension.createButton.${extensionType}`}
          />
        </Button>
      </Stack>
    </Stack>
  );
};

type Props = {
  dataCy?: string;
};

export const FirstExtensionBanner = ({ dataCy }: Props) => (
  <Stack data-cy={dataCy} spacing={2}>
    <Stack>
      <Typography variant="h6">
        <FormattedMessage id="containers.settings.extensions.intro.firstExtension.title" />
      </Typography>
      <Typography color="secondary">
        <FormattedMessage id="containers.settings.extensions.intro.firstExtension.text" />
      </Typography>
    </Stack>
    <Stack direction="row" spacing={2}>
      <FirstExtension extensionType={extensionFunctionType} />
      <FirstExtension extensionType={extensionWebhookType} />
    </Stack>
  </Stack>
);
